/* eslint-disable no-underscore-dangle */
import { CartStatus } from "constants/cart";
import { PRODUCT_TYPES } from "constants/product";
import { CurrencyTypes } from "constants/widget";
import Order from "models/Order";
import { formatPrice } from "utils/priceUtils";

/**
 * This class represents a Cart fetched from the API including all the needed business functions/methods related.
 */
class Cart {
  /** ---------------- Mappers functions ---------------- */
  /**
   * Map the cart data return by the API.
   */
  static fromAPI = (data) => {
    const rowGroups = data.row_groups?.map((rg) => Cart.mapRowGroupFromAPI(rg));
    const res = {
      uuid: data.uuid,
      canPayCashless: data._actions?.pay_cashless || false,
      canPayCashlessAfterTopUp: data._actions?.top_up_missing_amount || false,
      canPayOnline: data._actions?.pay_online || false,
      canPayOffline: data._actions?.pay_offline || false,
      createdAt: data.creation_date,
      emailAddress: data.email,
      formData: data.form_data,
      missingTotals: data.missing_totals?.map((t) => Cart.mapTotalFromAPI(t)),
      modifiedAt: data.modification_date,
      orders: data?.orders?.map((o) => Order.fromAPI(o)),
      payment: Cart.mapPaymentFromAPI(data.payment || {}),
      paymentLyriaUrl: data._actions?.pay?.href,
      paymentLyriaToken: data._actions?.pay?.data?.request_token,
      quantity: rowGroups?.reduce((acc, currentRowGroup) => acc + (currentRowGroup.quantity || 0), 0) || 0,
      rowGroups,
      status: Cart.mapStatusFromAPI(data.status),
      totals: data.totals?.map((t) => Cart.mapTotalFromAPI(t)),
      noFeesTotals: data.no_fees_totals?.map((t) => Cart.mapTotalFromAPI(t)),
      widgetUUID: data.widget_uuid,
      walletId: data.wallet_id ? parseInt(data.wallet_id) : null,
    };
    return res;
  };

  /**
   * Map the cart payment return by the API.
   */
  static mapPaymentFromAPI = (payment) => ({
    amount: payment?.amount ? parseInt(payment?.amount) : undefined,
    callbackUrlFailure: payment?.callback_urls?.failure,
    callbackUrlSuccess: payment?.callback_urls?.success,
    callbackUrlCancel: payment?.callback_urls?.cancel,
    createdAt: payment?.created_at,
    currencyCode: payment?.currency_trigram,
    error: payment?.error,
    id: payment?.id,
    reference: payment?.reference,
    status: payment?.status,
  });

  /**
   * Map the cart row groups return by the API.
   */
  static mapRowGroupFromAPI = (rowGroup) => ({
    id: parseInt(rowGroup.id),
    formData: rowGroup.form_data,
    rows: rowGroup.rows?.map((r) => ({
      id: parseInt(r.id),
      isRowUpdate: false,
      menuRows: r.menu_rows?.map((mr) => ({
        menuStepId: mr.menu_step_id,
        menuStepChoiceId: mr.menu_step_choice_id,
        quantity: mr.quantity,
      })),
      quantity: parseInt(r.quantity),
      productId: parseInt(r.product_id),
      isFee: r.product_type === PRODUCT_TYPES.FEE,
    })),
    quantity: rowGroup.rows
      ?.filter((row) => row.product_type !== PRODUCT_TYPES.FEE)
      ?.reduce((acc, currentRow) => acc + (currentRow.quantity || 0), 0) || 0,
    selectedPickUpLocationId: rowGroup.selected_pick_up_location_id,
    selectedPickUpEndAt: rowGroup.selected_pick_up_time_end_date,
    selectedPickUpStartAt: rowGroup.selected_pick_up_time_start_date,
    shopId: rowGroup.shop_id,
  });

  /**
   * Map the status return by the API.
   */
  static mapStatusFromAPI = (status) => {
    if (status === "W") {
      return CartStatus.WAITING;
    }
    if (status === "C") {
      return CartStatus.CANCELLED;
    }
    if (status === "V") {
      return CartStatus.VALIDATED;
    }
    return status;
  };

  /**
   * Map the totals return by the API.
   */
  static mapTotalFromAPI = (total) => ({
    amount: total.amount,
    currencyGroupId: total.currency_group_id,
    decimalPlaces: total.decimal_places,
    symbol: total.symbol,
  });

  /**
   *
   */
  static toAPI = (cart) => {
    const res = {
      form_data: cart.formData || {},
      rows: Cart.mapRowGroupsToRowsToAPI(cart.rowGroups),
      wallet_id: cart.walletId || null,
    };
    if (cart.emailAddress) {
      res.email = cart.emailAddress;
    }
    return res;
  };

  static mapRowGroupsToRowsToAPI = (rowGroups) => {
    const rows = [];
    rowGroups?.forEach((rg) => {
      rg.rows?.forEach((r) => {
        rows.push({
          id: r.menuRows?.length ? r.id : null,
          form_data: rg.formData,
          menu_rows: r.menuRows.map((mr) => ({
            menu_step_id: mr.menuStepId,
            menu_step_choice_id: mr.menuStepChoiceId,
            quantity: mr.quantity,
          })),
          selected_pick_up_location_id: rg.selectedPickUpLocationId,
          selected_pick_up_time_end_date: rg.selectedPickUpEndAt,
          selected_pick_up_time_start_date: rg.selectedPickUpStartAt,
          product_id: r.productId,
          quantity: r.quantity,
          shop_id: rg.shopId,
        });
      });
    });
    return rows;
  };

  /** ---------------- Business functions ---------------- */
  /**
   * This function will compute the new row groups according to what we want to update.
   * It's used to update the cart content, actually. We return the new row groups to assign to the cart data.
   * @param cart The cart to get the row groups to update and recompute.
   * @param shopId The ID of the shop the change comes from.
   * @param productId The ID of the product we want to update in the cart.
   * @param quantity The new quantity of product we want to update in the cart.
   * @param rowId The ID of the row if we want to specify the row to update
   * (especially for menu with different composition).
   * @param menuRows The new content of the menu to update.
   * @return {[{object}]} The new cart row groups updated, and we should assign to the cart to update it.
   * Note we don't update the cart in this function, the cart parameter is unchanged.
   */
  static updateCartRowGroups = (cart, shopId, productId, quantity, rowId = null, menuRows = []) => {
    const rowGroups = [];
    let mustCreateNewRowGroup = true;
    cart.rowGroups?.forEach((rg) => {
      if (rg.shopId === shopId) {
        mustCreateNewRowGroup = false;
        const rows = [];
        let mustCreateNewRow = true;
        rg.rows.forEach((r) => {
          if (r.productId === productId && (!rowId || r.id === rowId)) {
            if (quantity === 0) {
              mustCreateNewRow = false;
            } else {
              mustCreateNewRow = false;
              rows.push({
                ...r,
                menuRows: menuRows || [],
                quantity,
                productId,
              });
            }
          } else {
            rows.push(r);
          }
        });
        if (mustCreateNewRow && quantity) {
          rows.push({
            id: null,
            menuRows: menuRows || [],
            quantity,
            productId,
          });
        }
        rowGroups.push({
          ...rg,
          rows,
        });
      } else {
        rowGroups.push(rg);
      }
    });
    if (mustCreateNewRowGroup) {
      rowGroups.push({
        formData: null,
        rows: [
          {
            id: null,
            menuRows: menuRows || [],
            quantity,
            productId,
          },
        ],
        selectedPickUpLocationId: null,
        selectedPickUpEndAt: null,
        selectedPickUpStartAt: null,
        shopId,
      });
    }
    return rowGroups;
  };

  /** ---------------- Business functions ---------------- */
  /**
   * This function will compute the new row groups according to what we want to update about form and pick-up fields.
   * It's used to update the cart content, actually. We return the new row groups to assign to the cart data.
   * @param cart The cart to get the row groups to update and recompute.
   * @param shopId The ID of the shop the change comes from.
   * @param formData The object with the raw values of the form.
   * @return {[{object}]} The new cart row groups updated, and we should assign to the cart to update it.
   * Note we don't update the cart in this function, the cart parameter is unchanged.
   */
  static updateCartRowGroupsWithFormData = (cart, shopId, formData) => (
    cart.rowGroups?.map((rg) => {
      if (rg.shopId === shopId) {
        const formDataFiltered = { ...formData };
        let selectedPickUpLocationId = null;
        let selectedPickUpEndAt = null;
        let selectedPickUpStartAt = null;
        if (formDataFiltered.selected_pick_up_location) {
          selectedPickUpLocationId = parseInt(formDataFiltered.selected_pick_up_location);
          delete formDataFiltered.selected_pick_up_location;
        }
        if (formDataFiltered.selected_pick_up_timeslot) {
          [selectedPickUpStartAt, selectedPickUpEndAt] = formDataFiltered.selected_pick_up_timeslot.split("__");
          if (selectedPickUpStartAt === "default" || selectedPickUpEndAt === "default") {
            selectedPickUpStartAt = null;
            selectedPickUpEndAt = null;
          }
          delete formDataFiltered.selected_pick_up_date;
          delete formDataFiltered.selected_pick_up_timeslot;
        }
        const formDataClean = Object.fromEntries(
          Object
            .entries(formDataFiltered)
            .map(([key, value]) => {
              const newKey = key.split("__").slice(1).join("__");
              return [newKey, value];
            }),
        );
        return {
          ...rg,
          formData: formDataClean,
          selectedPickUpLocationId,
          selectedPickUpEndAt,
          selectedPickUpStartAt,
        };
      }
      return rg;
    })
  );

  /**
   * Get the list of shop IDs involved in the given cart.
   * @param {object} cart The cart data where the product are stored for each shop.
   * @return {[number]} The list of shop IDs involved in the cart.
   */
  static getShopIds = (cart) => cart?.rowGroups?.map((rg) => rg.shopId) || [];

  /**
   * This function will retrieve the quantity of product according to the parameters.
   * It's used to display content of the cart content, for each product.
   * @param cart The cart to analyze.
   * @param shopId The ID of the shop the product belongs to.
   * @param productId The ID of the product to retrieve the quantity.
   * @param rowId The ID of the row if we want to specify the row to retrieve
   * (especially for menu with different composition).
   * @return {number} The quantity of product in the cart.
   */
  static getProductQuantity = (cart, shopId, productId, rowId = null) => {
    if (rowId) {
      const matchingRow = cart.rowGroups
        ?.filter((rg) => rg.shopId === shopId)
        ?.map((rg) => rg.rows?.find((r) => r.id === rowId && r.productId === productId))
        ?.filter((res) => !!res);
      return matchingRow?.length ? matchingRow[0].quantity || 0 : 0;
    }
    let quantity = 0;
    cart.rowGroups
      ?.filter((rg) => rg.shopId === shopId)
      ?.forEach((rg) => {
        rg.rows
          ?.filter((r) => r.productId === productId)
          ?.forEach((r) => {
            quantity += r.quantity;
          });
      });
    return quantity;
  };

  /**
   * This function will retrieve the quantity of product according to the parameters.
   * It's used to display content of the cart content, for each product.
   * @param {object} cart The cart to analyze.
   * @param {number} shopId The ID of the shop the products belongs to.
   * @return {object} The quantity of product in the cart (productID as key and quantity as value).
   */
  static getProductQuantityPerProductId = (cart, shopId = null) => {
    const res = {};
    cart.rowGroups
      ?.filter((rg) => rg.shopId === shopId || !shopId)
      ?.forEach((rg) => {
        rg.rows?.forEach((r) => {
          res[r.productId] = (res[r.productId] || 0) + r.quantity;
        });
      });
    return res;
  };

  /**
   * Compute the total quantity of product in the cart. It may be filtered on the shop.
   * @param cart The cart data where the product are stored.
   * @param shopId The ID of the shop to filter the count.
   * @return {number} The total quantity of product in the cart (filtered or not on the shop).
   */
  static getProductTotalQuantity = (cart, shopId = null) => {
    const filteredRowGroup = shopId
      ? cart.rowGroups.filter((rg) => rg.shopId === shopId)
      : cart.rowGroups;

    return filteredRowGroup.reduce(
      (sumRowGroup, currentRG) => (
        sumRowGroup + (currentRG.rows?.reduce(
          (sumRow, currentRow) => sumRow + (currentRow.isFee ? 0 : currentRow.quantity),
          0,
        ) || 0)
      ),
      0,
    );
  };

  static computeAndFormatTotalForDisplay = (total, locale, exchangeRate, currencyTypeToDisplay) => {
    const currencyType = total.currencyGroupId === 1 ? currencyTypeToDisplay : CurrencyTypes.VIRTUAL;
    return formatPrice(exchangeRate, total.amount, total.decimalPlaces, total.symbol, locale, currencyType);
  };
}

export default Cart;
